import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import {
  PARTICIPANT_SEGMENTS,
  ParticipantEnum,
  ROUTES,
  TypeEnum
} from '@/constants/common';

const { SEARCH } = ROUTES;

type ValueType = string | string[] | number | number[] | boolean | null;
type ParamTypes = Partial<Record<string, ValueType>>;

type NavigateProps = {
  route?: string;
  params?: ParamTypes;
  participant?: ParticipantEnum;
  append?: boolean;
  shallow?: boolean;
  link?: boolean;
};

type GenerateUrlProps = {
  queryString: string | URLSearchParams;
  participant?: ParticipantEnum;
  route?: string;
};

/**
 * A custom hook for working with URL query params and navigation.
 * This hook is especially useful when there is a separation by roles like "vacancy" and "resume" and other cases.
 *
 * It provides helper functions to:
 * - build query strings,
 * - set and delete parameters,
 * - generate URLs for different roles and routes,
 * - navigate to new URLs.
 */

export const useNavigate = () => {
  const { push, replace } = useRouter();
  const existingSearchParams = useSearchParams();
  const pathName = usePathname();

  /**
   * Creates a query string from an object of parameters.
   *
   * @param params - An object with parameter names and their values. Values can be a string, number, boolean, or array.
   * @param append - If true, add these parameters to the existing search params.
   * @returns A query string like "key=value&anotherKey=value".
   */
  const buildQueryParams = (params: ParamTypes, append: boolean = false) => {
    const searchParams = new URLSearchParams(
      append ? existingSearchParams : undefined
    );

    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        const value = params[key];
        if (value) {
          const values = Array.isArray(value) ? value : [value];
          values.forEach(item => searchParams.append(key, item.toString()));
        }
      }
    }

    return searchParams.toString();
  };

  /**
   * Creates a complete URL string for navigation.
   *
   * @param route - Optional route path after the participant.
   * @param queryString - Optional query parameters.
   * @param participant - Optional user role type for the URL path.
   * @returns A full URL string with user role, page and query parameters.
   */
  const generateUrl = ({
    route = '',
    participant,
    queryString
  }: GenerateUrlProps) => {
    const currentQueryString =
      typeof queryString === 'string'
        ? queryString
        : new URLSearchParams(queryString ?? {}).toString();

    if (route) {
      const normalized = route.startsWith('/') ? route : `/${route}`;

      const withSearchPrefix =
        /^\/(search\/)?(vacancies|resumes)(?:\/|$)/i.test(normalized)
          ? normalized.startsWith('/search/')
            ? normalized
            : `${SEARCH}${normalized}`
          : normalized;

      return `${withSearchPrefix}${currentQueryString ? `?${currentQueryString}` : ''}`;
    }

    if (/^\/search\/(vacancies|resumes)(?:\/|$)/i.test(pathName)) {
      return `${pathName}${currentQueryString ? `?${currentQueryString}` : ''}`;
    }

    const lowerParticipant = String(
      participant ?? ParticipantEnum.VACANCY
    ).toLowerCase();
    const plural =
      lowerParticipant === TypeEnum.resume
        ? PARTICIPANT_SEGMENTS.RESUMES
        : PARTICIPANT_SEGMENTS.VACANCIES;

    return `${SEARCH}/${plural}${currentQueryString ? `?${currentQueryString}` : ''}`;
  };

  /**
   * Deletes a parameter from the current URL.
   *
   * @param key - The name of the parameter to delete.
   */
  const deleteParams = (key: string, route?: string) => {
    const params = new URLSearchParams(existingSearchParams.toString());
    params.delete(key);

    const path = generateUrl({
      route,
      queryString: params.toString()
    });

    replace(path, { scroll: false });
  };

  /**
   * Sets or updates parameters in the current URL.
   * Removes a parameter if its value is null or undefined.
   *
   * @param data - An object with parameter names and their new values.
   */
  const setParams = (data: ParamTypes, route?: string) => {
    const params = new URLSearchParams(existingSearchParams.toString());

    Object.entries(data).forEach(([key, value]) => {
      if (!value) return params.delete(key);

      if (Array.isArray(value)) {
        params.delete(key);

        value.forEach(v => {
          params.append(key, String(v));
        });
      } else {
        params.set(key, String(value));
      }
    });

    const path = generateUrl({
      route,
      queryString: params.toString()
    });

    replace(path, { scroll: false });
  };

  /**
   * Navigates to a new URL.
   *
   * @param props - An object with optional route, params, participant, and append flag.
   * If route is empty, it replaces the current URL.
   * Otherwise, it pushes a new history entry.
   */
  const navigate = (props: NavigateProps) => {
    const { route = '', shallow, params, append, participant, link } = props;

    const queryString = params
      ? buildQueryParams(params, append)
      : new URLSearchParams(existingSearchParams.toString());

    const pushParams = generateUrl({ route, participant, queryString });

    if (link) return pushParams;

    if (shallow) {
      window.history.replaceState(null, '', pushParams);
      return;
    }

    return route ? push(pushParams) : replace(pushParams);
  };

  return { navigate, deleteParams, setParams, generateUrl };
};
